/**
 * Traduce el fallo de una llamada a la API a una clave de i18n concreta.
 *
 * Hasta ahora los formularios mostraban siempre `common.accionError` ("ocurrieron
 * errores"), que no distingue entre un rechazo del WAF, un elemento borrado por otro
 * administrador y una red caída. Con textos largos eso era especialmente caro: el
 * autor no sabía si reintentar, recortar el contenido o avisar a alguien.
 *
 * @param {*} error error capturado de axios
 * @returns {string} clave para $t()
 */
export function errorMessageKey(error) {
    const response = error?.response

    // Sin respuesta: red caída, petición abortada o timeout de axios.
    if (!response) {
        return 'common.error.network'
    }

    const byStatus = {
        403: 'common.error.blocked',
        404: 'common.error.notFound',
        413: 'common.error.tooLarge'
    }

    return byStatus[response.status] ?? 'common.accionError'
}

/**
 * Mensajes concretos de un 422.
 *
 * La API responde en formato JSON:API (`{ errors: [{ detail }] }`) siempre que el
 * Accept lleva `+json`, que es justo lo que configura plugins/axios.js. Los
 * formularios, en cambio, leían `errors.body` / `errors.title` como si fuera el
 * formato plano de Laravel, así que el motivo del rechazo no se enseñaba nunca:
 * el administrador solo veía "ocurrieron errores".
 *
 * @param {*} error error capturado de axios
 * @returns {string[]} mensajes listos para mostrar (vacío si no los hay)
 */
export function validationMessages(error) {
    const errors = error?.response?.data?.errors

    if (!errors) {
        return []
    }

    if (Array.isArray(errors)) {
        return errors.map(e => e?.detail).filter(Boolean)
    }

    if (typeof errors === 'object') {
        return Object.keys(errors).reduce((acc, key) => {
            const value = errors[key]
            return acc.concat(Array.isArray(value) ? value : [value])
        }, []).filter(Boolean)
    }

    return []
}

export default errorMessageKey
